import React, { useEffect, useState } from 'react';
import { getEvidenceSummary, updateAssignment } from '../../services/stewardshipService';

const EVIDENCE_TYPE_LABELS = {
  community_observations: 'Community observations',
  harvest_records:        'Harvest records',
  consultation:           'Consultation',
  mnr_information:        'MNR information',
  elder_knowledge:        'Elder knowledge',
  harvester_knowledge:    'Harvester knowledge',
  scientific_source:      'Scientific source',
  habitat_information:    'Habitat information',
  project_impact:         'Project impact',
  other:                  'Other',
};

export default function EvidencePanel({ tenantId, assignmentId, onUpdate }) {
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [newEntry, setNewEntry] = useState({ type: '', summary: '', referenceId: '', sourceUrl: '', isRestricted: false });

  useEffect(() => {
    let alive = true;
    getEvidenceSummary(tenantId, assignmentId)
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, assignmentId]);

  const handleAdd = async () => {
    if (!newEntry.type) { setMsg('Choose an evidence type.'); return; }
    setSaving(true);
    setMsg('');
    try {
      const current = summary?.assignment?.evidenceSources || [];
      await updateAssignment(tenantId, assignmentId, {
        evidenceSources: [...current, newEntry],
      });
      setNewEntry({ type: '', summary: '', referenceId: '', sourceUrl: '', isRestricted: false });
      const r = await getEvidenceSummary(tenantId, assignmentId);
      setSummary(r.data);
      if (onUpdate) onUpdate();
      setMsg('Evidence source added.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to add evidence.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ fontSize: 13, color: '#94a3b8' }}>Loading evidence…</p>;

  const sources = summary?.assignment?.evidenceSources || [];
  const records = summary?.communityRecords;

  return (
    <div style={{ marginTop: 12 }}>
      {records?.total > 0 && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#166534' }}>
          <strong>{records.total} community harvest records</strong> for this species
          {records.earliest && <span style={{ color: '#64748b', marginLeft: 6 }}>from {new Date(records.earliest).toLocaleDateString()} to {new Date(records.latest).toLocaleDateString()}</span>}
        </div>
      )}

      {sources.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <strong style={{ fontSize: 12, color: '#475569', display: 'block', marginBottom: 6 }}>EVIDENCE SOURCES</strong>
          {sources.map((s, i) => (
            <div key={i} style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', marginBottom: 4, background: '#fafafa' }}>
              <span style={{ fontWeight: 600 }}>{EVIDENCE_TYPE_LABELS[s.type] || s.type}</span>
              {s.isRestricted && <span style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 4, padding: '1px 6px', fontSize: 10, marginLeft: 6 }}>Restricted</span>}
              {s.summary && <div style={{ color: '#475569', marginTop: 2 }}>{s.summary}</div>}
              {s.sourceUrl && <a href={s.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 11 }}>Source ↗</a>}
            </div>
          ))}
        </div>
      )}

      <strong style={{ fontSize: 12, color: '#475569', display: 'block', marginBottom: 6 }}>ADD EVIDENCE SOURCE</strong>
      <div style={{ display: 'grid', gap: 6 }}>
        <select
          value={newEntry.type}
          onChange={(e) => setNewEntry((p) => ({ ...p, type: e.target.value }))}
          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12 }}
        >
          <option value="">Select type…</option>
          {Object.entries(EVIDENCE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input
          placeholder="Brief summary"
          value={newEntry.summary}
          onChange={(e) => setNewEntry((p) => ({ ...p, summary: e.target.value }))}
          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12 }}
        />
        <input
          placeholder="Source URL (optional)"
          value={newEntry.sourceUrl}
          onChange={(e) => setNewEntry((p) => ({ ...p, sourceUrl: e.target.value }))}
          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={newEntry.isRestricted} onChange={(e) => setNewEntry((p) => ({ ...p, isRestricted: e.target.checked }))} />
          Mark as restricted (staff only, not shown to citizens)
        </label>
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving}
          style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}
        >
          {saving ? 'Saving…' : '+ Add Evidence'}
        </button>
        {msg && <div style={{ fontSize: 12, color: msg.includes('added') ? '#166534' : '#b91c1c' }}>{msg}</div>}
      </div>
    </div>
  );
}
